/**
 * ============================================================================
 * useMetricsWindow — assembles a metrics window from per-week cache entries.
 * ============================================================================
 *
 * Spec: metrics-window-caching (R1 reuse, R2 one generation, R5 prefetch).
 *
 * The cache unit is ONE reporting period, so a window is just a set of
 * subscriptions. Overlapping windows share weeks, two views wanting the same
 * week issue one request, and shifting a window fetches only the weeks it
 * gained. This hook:
 *
 *   - subscribes to each week (without calling a hook per week — a variable
 *     hook count would break the rules of hooks)
 *   - stitches the weeks into the single GetMetricsOutput selectors already take
 *   - refuses to render a window whose weeks come from different backend
 *     refreshes, refetching instead (R2)
 *   - prefetches neighbouring weeks once the visible window has resolved, so a
 *     nearby week switch is instant and speculative work never competes with
 *     the visible window (R5)
 */

import { useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import { metricsApi } from 'src/features/api/metricsApi';
import { recordWindowComplete } from 'src/metrics/performanceMetrics';
import {
  DataMode,
  DcrSnapshotData,
  GetMetricsOutput,
} from 'src/types/metricsApi.types';

/** How many weeks either side of the visible window to warm (R5.2). */
export const PREFETCH_NEIGHBOUR_WEEKS = 4;

/**
 * How many neighbour weeks are warmed at a time. Deliberately small: visible
 * weeks are dispatched all at once (they are what the user is waiting for),
 * while speculative weeks trickle so they cannot fill the browser's connection
 * pool ahead of the user's next real request.
 */
export const PREFETCH_CONCURRENCY = 2;

/** One speculative fetch: a week in a given data mode. */
interface WarmTarget {
  dataMode: DataMode;
  reportingPeriod: string;
}

interface UseMetricsWindowArgs {
  id: string;
  dataMode: DataMode;
  /** The weeks this view renders, in display order. */
  weeks: string[] | undefined;
  /**
   * The week universe from getWeekNumbers, used to pick neighbours to prefetch.
   * Omit to disable neighbour prefetching.
   */
  universe?: string[];
  /**
   * Whole windows to warm once this one has resolved, in priority order, before
   * any neighbour week (R5.7). Pages declare these because they know where the
   * user is likely to go next, and because windows differ by mode: a snapshot
   * anchors on the last completed week and has no forward weeks.
   *
   * Typical targets are this view in the other data mode, and the window of the
   * page this one links to. Each must be a WHOLE window — a partly warmed one
   * still blocks, since a window is withheld until every week is present.
   */
  alsoWarm?: Array<{ dataMode: DataMode; weeks: string[] }>;
}

export interface MetricsWindowResult {
  /** Stitched window, or undefined until every visible week has data. */
  data: GetMetricsOutput | undefined;
  /** True while any visible week has no data yet. */
  isLoading: boolean;
  /** True while any visible week is in flight (including a refresh reload). */
  isFetching: boolean;
  /** First error across the visible weeks, if any. */
  error: unknown;
  /** True while reloading because the backend published a new refresh (R2.4). */
  isReloadingForRefresh: boolean;
  refetch: () => void;
}

interface WeekState {
  data?: GetMetricsOutput;
  isFetching: boolean;
  error?: unknown;
}

export function useMetricsWindow({
  id,
  dataMode,
  weeks,
  universe,
  alsoWarm,
}: UseMetricsWindowArgs): MetricsWindowResult {
  const dispatch = useAppDispatch();
  /**
   * `undefined` and `[]` mean different things and must not be collapsed:
   *   undefined → the caller has not resolved its window yet (it is waiting on
   *               getWeekNumbers), so this hook is still loading.
   *   []        → there genuinely are no weeks to fetch; a settled state.
   * Treating the first as "not loading" makes every page render its empty state
   * for a beat BEFORE the spinner, because there is no data and, apparently,
   * nothing in flight.
   */
  const windowUnresolved = weeks === undefined;
  const visibleWeeks = useMemo(() => weeks ?? [], [weeks]);
  const visibleKey = visibleWeeks.join('|');
  // Identity for the effect deps: pages rebuild the alsoWarm targets on every
  // render, so the contents decide when warming restarts, not the reference.
  const alsoWarmKey = (alsoWarm ?? [])
    .map((target) => `${target.dataMode}:${target.weeks.join('|')}`)
    .join(';');

  // ── Subscriptions for the visible weeks ──
  // Held in a ref and released when the window changes, so RTK keeps the
  // entries alive exactly while some view needs them.
  const subscriptions = useRef<Map<string, { unsubscribe: () => void }>>(
    new Map(),
  );

  useEffect(() => {
    const wanted = new Set(visibleWeeks);
    const held = subscriptions.current;

    held.forEach((subscription, week) => {
      if (!wanted.has(week)) {
        subscription.unsubscribe();
        held.delete(week);
      }
    });

    visibleWeeks.forEach((reportingPeriod) => {
      if (held.has(reportingPeriod)) return;
      held.set(
        reportingPeriod,
        dispatch(
          metricsApi.endpoints.getMetricsWeek.initiate({
            id,
            dataMode,
            reportingPeriod,
          }),
        ) as unknown as { unsubscribe: () => void },
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, dataMode, visibleKey]);

  // Release everything on unmount.
  useEffect(
    () => () => {
      subscriptions.current.forEach((subscription) =>
        subscription.unsubscribe(),
      );
      subscriptions.current.clear();
    },
    [],
  );

  // ── Read the per-week entries out of the cache ──
  // The selector builds a fresh record every call, so it is paired with an
  // equality check on the week entries. Without it the hook would re-render on
  // every dispatched action, including ones that touch no week in this window.
  const weekStates = useAppSelector<Record<string, WeekState>>((state) => {
    const result: Record<string, WeekState> = {};
    for (const reportingPeriod of visibleWeeks) {
      const entry = metricsApi.endpoints.getMetricsWeek.select({
        id,
        dataMode,
        reportingPeriod,
      })(state as never);
      result[reportingPeriod] = {
        data: entry.data,
        // `select` exposes status, not the hook's isFetching flag.
        isFetching: entry.status === 'pending',
        error: entry.error,
      };
    }
    return result;
  }, sameWeekStates);

  const refetch = () => {
    visibleWeeks.forEach((reportingPeriod) => {
      dispatch(
        metricsApi.endpoints.getMetricsWeek.initiate(
          { id, dataMode, reportingPeriod },
          { forceRefetch: true },
        ),
      );
    });
  };

  // ── Generation guard (R2) ──
  // Weeks are fetched independently, so a backend refresh between two fetches
  // could leave one week newer than the rest. Comparing week-over-week numbers
  // across refreshes is invalid, so the stale weeks are refetched instead of
  // rendered.
  //
  // A week that returned NO rows is excluded: it has no values to compare, so
  // it cannot be stale, and its timestamp is not a generation. The backend
  // reports a sentinel for an empty period (dev returns 1970-01-01T00:00:00Z),
  // and counting that as an older refresh would withhold the window forever.
  // Snapshot windows hit this routinely — a capture exists only for weeks that
  // have closed, so the running week and any unpublished week come back empty.
  const generationOf = (week: string): string | undefined => {
    const weekData = weekStates[week]?.data;
    if (!weekData || weekData.data.length === 0) return undefined;
    return weekData.lastRefreshedAt || undefined;
  };

  const generations = visibleWeeks
    .map(generationOf)
    .filter((stamp): stamp is string => !!stamp);
  const newestGeneration = generations.reduce(
    (newest, stamp) => (stamp > newest ? stamp : newest),
    '',
  );
  // SNAPSHOT windows are EXEMPT from the guard (R2.6): each snapshot week is
  // its own immutable capture, so per-week timestamps differ BY DESIGN — they
  // are capture metadata, not generations. Comparing them declares every
  // multi-week snapshot window permanently "mixed", withholds it, and
  // refetches in an infinite loop while the page shows its empty state.
  const mixedGenerations =
    dataMode !== 'SNAPSHOT' &&
    generations.some((stamp) => stamp !== newestGeneration);

  useEffect(() => {
    if (!mixedGenerations) return;
    visibleWeeks.forEach((reportingPeriod) => {
      const stamp = generationOf(reportingPeriod);
      if (stamp && stamp !== newestGeneration) {
        dispatch(
          metricsApi.endpoints.getMetricsWeek.initiate(
            { id, dataMode, reportingPeriod },
            { forceRefetch: true },
          ),
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mixedGenerations, newestGeneration, visibleKey, id, dataMode]);

  const anyFetching = visibleWeeks.some((week) => weekStates[week]?.isFetching);
  const missingData = visibleWeeks.some((week) => !weekStates[week]?.data);
  const firstError = visibleWeeks
    .map((week) => weekStates[week]?.error)
    .find((error) => !!error);

  // ── Stitch, in requested order (R1.5) ──
  const data = useMemo((): GetMetricsOutput | undefined => {
    if (visibleWeeks.length === 0 || missingData || mixedGenerations)
      return undefined;
    const periods: DcrSnapshotData[] = [];
    let dashboardId: GetMetricsOutput['dashboardId'] = id;
    let reviewType: GetMetricsOutput['reviewType'] = 'WBR';
    for (const week of visibleWeeks) {
      const weekData = weekStates[week]!.data!;
      dashboardId = weekData.dashboardId ?? dashboardId;
      reviewType = weekData.reviewType ?? reviewType;
      for (const period of weekData.data) periods.push(period);
    }
    return {
      data: periods,
      dashboardId,
      reviewType,
      dataMode,
      lastRefreshedAt: newestGeneration,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visibleKey,
    missingData,
    mixedGenerations,
    newestGeneration,
    dataMode,
    weekStates,
  ]);

  // ── Render-gate timing (client-side observability) ──
  // Measures the user-facing "window requested → every visible week present"
  // duration — the exact span the page shows loaded chrome with no data. A
  // cache-hit window reports ~0ms; that is the experience, not noise. Reported
  // once per distinct window (visibleKey).
  const gateTimer = useRef<{ key: string; startedAt: number; reported: boolean }>({
    key: '',
    startedAt: 0,
    reported: true,
  });
  // The timer key must encode everything that changes WHAT is being loaded:
  // the weeks AND the data mode (a Live↔Snapshot toggle can reuse the same
  // week tokens) AND the dashboard id.
  const gateKey = `${id}#${dataMode}#${visibleKey}`;
  if (gateTimer.current.key !== gateKey) {
    gateTimer.current = { key: gateKey, startedAt: performance.now(), reported: false };
  }
  useEffect(() => {
    if (data && !gateTimer.current.reported && gateTimer.current.key === gateKey) {
      gateTimer.current.reported = true;
      recordWindowComplete(
        dataMode,
        visibleWeeks.length,
        performance.now() - gateTimer.current.startedAt,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, gateKey, dataMode]);

  // ── Warm what the user is most likely to ask for next (R5) ──
  //
  // Two kinds of target, in this order:
  //
  //  1. Whole windows the page nominated (`alsoWarm`) — this view in the other
  //     data mode, and the window of the page it links to (R5.7). These go
  //     first because one click swaps every week on screen, whereas a week
  //     nudge needs a single week.
  //  2. Weeks either side of the visible window (R5.1). Only pages with a week
  //     control pass a `universe`, so only they warm neighbours.
  //
  // Two things keep speculative work from hurting the user: it starts only
  // after every visible week has arrived (R5.3), and it goes a FEW weeks at a
  // time, stopping as soon as the visible window changes. Dispatching every
  // target at once would queue dozens of requests into the browser's
  // connection pool for this host, and the next thing the user actually asks
  // for would wait behind them — the opposite of what prefetching is for.
  const settled = !anyFetching && !missingData && !mixedGenerations;
  useEffect(() => {
    if (!settled || visibleWeeks.length === 0) return;
    let abandoned = false;

    const targets: WarmTarget[] = [];
    for (const target of alsoWarm ?? []) {
      for (const reportingPeriod of target.weeks) {
        targets.push({ dataMode: target.dataMode, reportingPeriod });
      }
    }
    if (universe?.length) {
      for (const reportingPeriod of neighbourWeeks(
        universe,
        visibleWeeks,
        PREFETCH_NEIGHBOUR_WEEKS,
      )) {
        targets.push({ dataMode, reportingPeriod });
      }
    }

    const warm = async (): Promise<void> => {
      for (let i = 0; i < targets.length; i += PREFETCH_CONCURRENCY) {
        if (abandoned) return;
        const batch = targets.slice(i, i + PREFETCH_CONCURRENCY);
        await Promise.all(
          batch.map(async (target) => {
            // Subscribe, await, then release: the entry stays cached for
            // keepUnusedDataFor without this view owning it.
            const subscription = dispatch(
              metricsApi.endpoints.getMetricsWeek.initiate({
                id,
                dataMode: target.dataMode,
                reportingPeriod: target.reportingPeriod,
              }),
            );
            try {
              await subscription;
            } catch {
              // A prefetch is speculative: if it fails the week is simply
              // fetched on demand later (R5.4).
            } finally {
              (
                subscription as unknown as { unsubscribe: () => void }
              ).unsubscribe();
            }
          }),
        );
      }
    };
    // IDLE GATE: "settled" fires at the exact moment the heaviest main-thread
    // work begins — the table (ag-grid) renders, LCP paints, comment fetches
    // go out. Kicking warming off inside that burst made speculative requests
    // compete with user-visible work for the connection pool and the main
    // thread (prod: other-mode warming parses ~25MB of payloads). Wait for an
    // idle period instead; the 2s timeout guarantees warming still happens on
    // busy pages. Fallback covers jsdom/Safari where rIC is missing.
    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(() => void warm(), {
        timeout: 2000,
      });
    } else {
      timeoutHandle = setTimeout(() => void warm(), 200);
    }

    return () => {
      abandoned = true;
      if (idleHandle !== undefined) window.cancelIdleCallback?.(idleHandle);
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, visibleKey, id, dataMode, universe?.length, alsoWarmKey]);

  return {
    data,
    // Loading covers "the window is not known yet", "the window is known but
    // some week has no data", AND "the guard is withholding the window while
    // it reloads a stale week" (mixedGenerations ⇒ data === undefined with
    // fetches in flight). Omitting the first caused the empty state to flash
    // before the spinner; omitting the last rendered "no data" empty states
    // over an in-progress reload.
    isLoading:
      (windowUnresolved ||
        (visibleWeeks.length > 0 && (missingData || mixedGenerations))) &&
      !firstError,
    isFetching: anyFetching,
    error: firstError,
    isReloadingForRefresh: mixedGenerations,
    refetch,
  };
}

/**
 * Two window reads are equivalent when they cover the same weeks and no week's
 * cache entry changed. `data` and `error` are compared by reference because RTK
 * replaces them on change and keeps them otherwise.
 */
function sameWeekStates(
  left: Record<string, WeekState>,
  right: Record<string, WeekState>,
): boolean {
  const leftWeeks = Object.keys(left);
  if (leftWeeks.length !== Object.keys(right).length) return false;
  for (let i = 0; i < leftWeeks.length; i++) {
    const week = leftWeeks[i];
    const a = left[week];
    const b = right[week];
    if (!b) return false;
    if (
      a.data !== b.data ||
      a.error !== b.error ||
      a.isFetching !== b.isFetching
    )
      return false;
  }
  return true;
}

/**
 * Weeks just outside the visible window, nearest first, bounded by `count` on
 * each side (R5.2). Nearest-first matters: a user nudging the week control one
 * step should hit a warm entry even if the prefetch is still running.
 */
export function neighbourWeeks(
  universe: string[],
  visibleWeeks: string[],
  count: number,
): string[] {
  const visible = new Set(visibleWeeks);
  let first = -1;
  let last = -1;
  universe.forEach((week, index) => {
    if (!visible.has(week)) return;
    if (first === -1) first = index;
    last = index;
  });
  if (first === -1) return [];

  const result: string[] = [];
  for (let step = 1; step <= count; step++) {
    const before = first - step;
    const after = last + step;
    if (after < universe.length) result.push(universe[after]);
    if (before >= 0) result.push(universe[before]);
  }
  return result;
}
